"use client";

// eslint-disable-next-line next-intl/only-next-intl-button-link
import { Button, ButtonGroup, ButtonLink } from "@sikt/sds-button";
import { Heading1, Paragraph } from "@sikt/sds-core";
import { HomeLandingIcon, RefreshIcon } from "@sikt/sds-icons";

import { Locale } from "@/src/intl/locale";

import ErrorNavigationWrapper from "./error-navigation-wrapper";
import css from "./error-page-content.module.css";

const getDefaultTitle = (locale: Locale): string => {
  switch (locale) {
    case "nb":
      return "Det oppsto en feil";
    case "nn":
      return "Det oppstod ein feil";
    case "en":
      return "An error occurred";
  }
};

const getSubtitle = (locale: Locale): string => {
  switch (locale) {
    case "nb":
      return "Sjekk om nettadressen stemmer og prøv å laste inn siden på nytt. Dersom feilen vedvarer, ta kontakt med brukerstøtte.";
    case "nn":
      return "Sjekk om nettadressa stemmer og prøv å laste inn sida på nytt. Dersom feilen vedvarar, ta kontakt med brukarstøtte.";
    case "en":
      return "Check if the web address is correct and try reloading the page. If the error persists, contact user support.";
  }
};

const getErrorCodeLabel = (locale: Locale): string => {
  switch (locale) {
    case "nb":
      return "Feilkode";
    case "nn":
      return "Feilkode";
    case "en":
      return "Error code";
  }
};

const getTryAgainText = (locale: Locale): string => {
  switch (locale) {
    case "nb":
      return "Prøv på nytt";
    case "nn":
      return "Prøv på nytt";
    case "en":
      return "Try again";
  }
};

const getGoToHomePageText = (locale: Locale): string => {
  switch (locale) {
    case "nb":
      return "Gå til startsiden";
    case "nn":
      return "Gå til startsida";
    case "en":
      return "Go to the home page";
  }
};

interface ErrorPageContentProps {
  locale: Locale;
  title?: string;
  errorCode?: number;
  showRetryButton?: boolean;
  onRetry?: () => void;
}

const ErrorPageContent = ({
  locale,
  title,
  errorCode,
  showRetryButton = false,
  onRetry,
}: ErrorPageContentProps) => {
  const displayTitle = title ?? getDefaultTitle(locale);
  const codeDisplay = errorCode
    ? `${getErrorCodeLabel(locale)}: ${errorCode}`
    : undefined;

  return (
    <ErrorNavigationWrapper>
      <section className={css.errorContainer}>
        <Heading1 variant="editorial" size="l">
          {displayTitle}
        </Heading1>
        <Paragraph variant="body" size="l">
          {getSubtitle(locale)}
        </Paragraph>
        {codeDisplay && (
          <Paragraph variant="body" size="l">
            {codeDisplay}
          </Paragraph>
        )}
        <ButtonGroup>
          <ButtonLink
            variant={showRetryButton ? "subtle" : "strong"}
            icon={<HomeLandingIcon />}
            href={`/${locale}`}
          >
            {getGoToHomePageText(locale)}
          </ButtonLink>
          {showRetryButton && (
            <Button variant="strong" onClick={onRetry} icon={<RefreshIcon />}>
              {getTryAgainText(locale)}
            </Button>
          )}
        </ButtonGroup>
      </section>
    </ErrorNavigationWrapper>
  );
};

export default ErrorPageContent;
